<template>
  <FormPage
    title="Менеджеры"
    description="Список всех менеджеров в системе"
  >
    <template #controls>
      <button
        class="btn btn-primary"
        type="button"
        @click="router.push({ name: 'ManagerCreate' })"
      >+ Создать менеджера</button>
    </template>

    <FormCard>
      <FormGrid :cols="3">
        <FormField label="Общий поиск">
          <input
            v-model.trim="filters.query"
            type="text"
            placeholder="Имя, телефон, email"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по имени">
          <input
            v-model.trim="filters.queryName"
            type="text"
            placeholder="Имя"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по телефону">
          <input
            v-model.trim="filters.queryPhone"
            type="text"
            placeholder="Телефон"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <FormField label="Поиск по email">
          <input
            v-model.trim="filters.queryEmail"
            type="text"
            placeholder="Email"
            @input="debouncedFetchFirstPage"
          />
        </FormField>

        <div class="filters-actions">
          <button class="btn" type="button" @click="resetFilters">Сбросить</button>
        </div>
      </FormGrid>
    </FormCard>

    <FormCard>
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="onPageChanged"
      />

      <my-loader v-if="isLoading" />

      <div v-else-if="hasError && !managers.length" class="state-block error-block">
        Ошибка загрузки списка менеджеров. Попробуйте позже.
      </div>

      <div v-else class="content">
        <div v-if="hasError && managers.length" class="inline-error">
          Не удалось обновить список. Показаны предыдущие данные.
        </div>

        <div v-if="managers.length" class="table-scroll">
          <table class="managers-table">
            <thead>
              <tr>
                <th class="col-id">ID</th>
                <th>Имя</th>
                <th>Телефон</th>
                <th>Email</th>
                <th>Компания</th>
                <th class="col-actions">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in managers"
                :key="row.id"
                class="row-card"
                @click="onRowClick(row)"
              >
                <td :title="String(row.id)">{{ row.id }}</td>
                <td :title="row.name">{{ row.name || '—' }}</td>
                <td :title="row.phone">{{ row.phone || '—' }}</td>
                <td :title="row.email">{{ row.email || '—' }}</td>
                <td :title="row.company?.name">{{ row.company?.name || '—' }}</td>
                <td class="col-actions">
                  <button
                    class="mini-btn"
                    type="button"
                    @click.stop="onRowClick(row)"
                  >Просмотр</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="state-block empty-state">
          Нет менеджеров
        </div>
      </div>
    </FormCard>
  </FormPage>
</template>

<script>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import FormPage from '@/components/formKit/FormPage.vue'
import FormCard from '@/components/formKit/FormCard.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import FormField from '@/components/formKit/FormField.vue'
import { listManagers } from '@/backend/managerApi'

export default {
  name: 'ManagerList',
  components: {
    FormPage,
    FormCard,
    FormGrid,
    FormField,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()

    const managers = ref([])
    const isLoading = ref(false)
    const hasError = ref(false)
    const currentPage = ref(1)
    const totalPages = ref(1)

    const filters = reactive({
      query: route.query.query || '',
      queryName: route.query.queryName || '',
      queryPhone: route.query.queryPhone || '',
      queryEmail: route.query.queryEmail || '',
    })

    const filterParams = () => {
      const params = {}
      if (filters.query) params.search = filters.query.toLowerCase()
      if (filters.queryName) params.name_contains = filters.queryName.toLowerCase()
      if (filters.queryPhone) params.phone_contains = filters.queryPhone.toLowerCase()
      if (filters.queryEmail) params.email_contains = filters.queryEmail.toLowerCase()
      return params
    }

    const updateURL = () => {
      router.replace({ query: { ...filters } }).catch(() => {})
    }

    const loadData = async (page) => {
      isLoading.value = true
      hasError.value = false
      try {
        const data = await listManagers({ page, per_page: 20, ...filterParams() })
        managers.value = Array.isArray(data?.managers) ? data.managers : []
        currentPage.value = data?.current_page || page
        totalPages.value = data?.total_pages || 1
      } catch (e) {
        console.error('[ManagerList] listManagers failed', e)
        hasError.value = true
      } finally {
        isLoading.value = false
      }
    }

    const fetchFirstPage = () => {
      loadData(1)
      updateURL()
    }

    const debouncedFetchFirstPage = useDebounceFn(fetchFirstPage, 300)

    const onPageChanged = (nextPage) => {
      loadData(nextPage)
    }

    const resetFilters = () => {
      filters.query = ''
      filters.queryName = ''
      filters.queryPhone = ''
      filters.queryEmail = ''
      fetchFirstPage()
    }

    const onRowClick = (row) => {
      if (!row?.id) return
      router.push({ name: 'ManagerDetail', params: { id: row.id } })
    }

    onMounted(() => {
      const pageFromUrl = Number(route.query.page) || 1
      loadData(pageFromUrl)
    })

    return {
      router,
      managers,
      isLoading,
      hasError,
      currentPage,
      totalPages,
      filters,
      debouncedFetchFirstPage,
      onPageChanged,
      resetFilters,
      onRowClick,
    }
  },
}
</script>

<style scoped>
.btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  height: 32px;
  border-radius: 4px;
  padding: 0 12px;
}

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.btn-primary {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
  color: #fff;
}

.filters-actions {
  display: flex;
  align-items: flex-end;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 12px;
  width: 100%;
}

.state-block {
  padding: 32px;
  color: #667085;
  font-size: 14px;
}

.error-block {
  color: #c0392b;
}

.empty-state {
  text-align: center;
}

.inline-error {
  width: 100%;
  padding: 8px 10px;
  border-radius: 4px;
  background: #fff4e5;
  color: #8a5a00;
  font-size: 13px;
}

.table-scroll {
  width: 100%;
  position: relative;
  overflow-x: auto;
}

.managers-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  font-size: 13px;
  min-width: 100%;
}

.managers-table th,
.managers-table td {
  height: 32px;
  padding: 6px 10px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.managers-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f7f8fa;
  color: #444;
  font-weight: bold;
}

.managers-table td {
  font-weight: bold;
}

.col-id {
  width: 60px;
}

.col-actions {
  width: 110px;
  text-align: center;
}

.mini-btn {
  border: 1px solid #d7dce2;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 12px;
  height: 26px;
  border-radius: 4px;
  padding: 0 10px;
}

.mini-btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.row-card {
  cursor: pointer;
}

.row-card:hover {
  background-color: rgba(224, 187, 72, 0.4);
}
</style>
